import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { useAuthState } from "react-firebase-hooks/auth";
import { doc, getDoc, collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import LandingNavbar from "../components/landingNavbar";
import Footer from "../components/footer";
import LoadingSpinner from "../components/loadingSpinner";
import { createDesignFromTemplate } from "../services/createDesignFromTemplate";
import { toast } from "react-hot-toast";
import { PENDING_TEMPLATE_STORAGE_KEY } from "../utils/navigation";
import { getUserPlanData } from "../services/firebase";

const DEFAULT_THUMB =
  "https://firebasestorage.googleapis.com/v0/b/cloud-drill-design.appspot.com/o/snapshots%2FuHksLWYzwdcq09JNvegv.png?alt=media&token=4e69d0f9-99e4-48e8-ba82-71e55f306830";
function buildAuthRedirect(pathname) {
  const q = `?redirect=${encodeURIComponent(pathname)}`;
  return { login: `/login${q}`, register: `/register${q}` };
}

export default function TemplateLanding() {
  const { templateId } = useParams();
  const [user, authLoading] = useAuthState(auth);
  const [template, setTemplate] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();
  const [uploadingAvailable, setUploadingAvailable] = useState(true);
  const [isPlanInactive, setIsPlanInactive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pastDue, setPastDue] = useState(false);
  const [isPlanCanceled, setIsPlanCanceled] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [storageLimit, setStorageLimit] = useState(50);
  const [drills, setDrills] = useState([]);
  const pathname =
    typeof window !== "undefined"
      ? window.location.pathname
      : `/templates/${templateId}`;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!templateId) {
        setLoadError("missing_id");
        setFetching(false);
        return;
      }
      setFetching(true);
      setLoadError(null);
      try {
        const ref = doc(db, "drillTemplates", templateId);
        const snap = await getDoc(ref);
        if (cancelled) return;
        if (!snap.exists()) {
          setLoadError("not_found");
          setTemplate(null);
        } else {
          setTemplate({ id: snap.id, ...snap.data() });
        }
      } catch (e) {
        console.error("Error loading template:", e);
        if (!cancelled) setLoadError("fetch_failed");
      } finally {
        if (!cancelled) setFetching(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [templateId]);

  const fetchDrills = async (userId) => {
    const designsQuery = query(
      collection(db, "designs"),
      where("createdBy", "==", userId)
    );

    const querySnapshot = await getDocs(designsQuery);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  };
  const checkUploadStatus = async () => {
    try {
      const planData = await getUserPlanData(auth.currentUser.uid);
      if (!planData) return;

      const drillsData = await fetchDrills(auth.currentUser.uid);
      setDrills(drillsData); // optional UI state

      const { planKey, planActive } = planData;

      // FREE PLAN
      if (planKey === null) {
        const limit = 5;
        setStorageLimit(limit);

        if (drillsData.length >= limit) {
          setUploadingAvailable(false);
        } else {
          setUploadingAvailable(true);
        }

        setIsPlanInactive(false);
        return;
      }

      // INACTIVE PLAN
      if (!planActive) {
        setUploadingAvailable(false);
        setIsPlanInactive(true);

        if (planKey === "past_due") setPastDue(true);
        if (planKey === "canceled") setIsPlanCanceled(true);

        return;
      }

      // ACTIVE PAID PLAN
      setUploadingAvailable(true);
      setIsPro(true);

    } catch (error) {
      console.error("Error checking upload status:", error);
    }
  };


  useEffect(() => {
    setLoading(true);
    if (user) {
      const fetchData = async () => {

        await fetchDrills(auth.currentUser.uid);
        await checkUploadStatus();

      }
      fetchData();
      checkUploadStatus();

    }
    setLoading(false);
  }, [user]);

  useEffect(() => {
    if (authLoading) return;
    if (!templateId) return;
    if (!user) {
      try {
        sessionStorage.setItem(PENDING_TEMPLATE_STORAGE_KEY, templateId);
      } catch (_) { }
    } else {
      try {
        sessionStorage.removeItem(PENDING_TEMPLATE_STORAGE_KEY);
      } catch (_) { }
    }
  }, [user, authLoading, templateId]);

  const handleStartFromTemplate = useCallback(async () => {
    if (!templateId || !user) return;
    if (uploadingAvailable === false) return toast.error("You have reached your upload limit. Please upgrade to a paid plan to continue.");
    setCreating(true);
    try {
      const newId = await createDesignFromTemplate(templateId, user.uid);
      navigate(`/designs/${newId}`);
    } catch (e) {
      console.error(e);
      toast.error("Could not create a design from this template. Try again.");
    } finally {
      setCreating(false);
    }
  }, [templateId, user, navigate]);

  const authLinks = buildAuthRedirect(pathname);


  if (fetching || authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex flex-col">
        <LandingNavbar />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (loadError === "fetch_failed") {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex flex-col">
        <Helmet>
          <title>Error | DrillFlo</title>
        </Helmet>
        <LandingNavbar />
        <div className="flex-1 max-w-lg mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Something went wrong
          </h1>
          <p className="text-gray-600 mb-8">
            We could not load this template. Please try again later.
          </p>
          <Link
            to="/marching-band-drill-design-software"
            className="text-blue-600 font-medium hover:text-blue-800"
          >
            Back to DrillFlo
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  if (loadError === "not_found" || !template) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex flex-col">
        <Helmet>
          <title>Template not found | DrillFlo</title>
        </Helmet>
        <LandingNavbar />
        <div className="flex-1 max-w-lg mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">
            Template not found
          </h1>
          <p className="text-gray-600 mb-8">
            This template does not exist or is no longer available.
          </p>
          <Link
            to="/marching-band-drill-design-software"
            className="text-blue-600 font-medium hover:text-blue-800"
          >
            Back to DrillFlo
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const title = template.title || "Drill template";
  const thumbnail = template.snapshotURL || DEFAULT_THUMB;
  const createdBy =
    template.createdByName || template.createdBy || "DrillFlo";

  return (
    <div className="min-h-screen bg-gray-50 pt-20 flex flex-col font-plusJakarta">
      <Helmet>
        <title>{title} — Template | DrillFlo</title>
        <meta
          name="description"
          content={`Start from the “${title}” drill template in DrillFlo.`}
        />
      </Helmet>
      <LandingNavbar />

      <main className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 w-full">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="aspect-video bg-gray-100">
            <img
              src={thumbnail}
              alt={title}
              className="w-full h-full object-cover"
            />
          </div>
          <div className="p-8 sm:p-10">
            <p className="text-sm text-blue-600 font-medium mb-2">Template</p>
            <h1 className="text-3xl font-bold text-gray-900 mb-3">{title}</h1>
            <p className="text-gray-600 mb-8">
              {user
                ? "Create your own copy to edit in the DrillFlo editor."
                : "Create a free account to copy this template into your workspace and start editing."}
            </p>
            <p className="text-sm text-gray-500 mb-8">Provided by {createdBy}</p>

            {user && uploadingAvailable ? (
              <button
                type="button"
                onClick={handleStartFromTemplate}
                disabled={creating}
                className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-blue-600 to-blue-800 hover:opacity-95 disabled:opacity-60 shadow-sm"
              >
                {creating ? "Creating…" : "Start with this template"}
              </button>
            ) : user && uploadingAvailable === false ? (
              <button
                type="button"
                // onClick={() => setShowUpgradeModal(true)}
                className="w-full sm:w-auto inline-flex justify-center items-center px-8 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-blue-600 to-blue-800 hover:opacity-95 disabled:opacity-60 shadow-sm"
              >
                Upgrade to DrillFlo Pro
              </button>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-800 font-medium">
                  Sign in or create an account to use this template
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link
                    to={authLinks.register}
                    className="inline-flex justify-center items-center px-8 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-blue-600 to-purple-600 hover:opacity-95 shadow-sm"
                  >
                    Create an account
                  </Link>
                  <Link
                    to={authLinks.login}
                    className="inline-flex justify-center items-center px-8 py-3 rounded-lg font-semibold border border-gray-300 text-gray-800 hover:bg-gray-50"
                  >
                    Sign in
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        <p className="mt-8 text-center">
          <Link
            to="/marching-band-drill-design-software"
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            ← DrillFlo home
          </Link>
        </p>
      </main>

      <Footer />
    </div>
  );
}
